import type { ContractTermsDto } from '@customTypes/types';
import { FlightPaymentType } from '@customTypes/types';
import { useForm, zodResolver } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import { contractTermsSchema } from '@/pages/club/onboard/components/stages/contract-terms/constants/schema';
import {
  useClubProfileContractTermsMutation,
  useClubProfileContractTermsQuery
} from '@/shared/api/requests/club';

export const useContractTermsPage = () => {
  const { formatMessage } = useIntl();
  const { id: clubId } = useParams({ from: '/clubOwner/$id' });
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: '/clubOwner/$id/onboard/$step' });
  const contractTermsQuery = useClubProfileContractTermsQuery({ clubId });
  const contractTermsMutation = useClubProfileContractTermsMutation();

  const form = useForm<{
    data: ContractTermsDto;
    additional: {
      compensationForOneMonth: boolean;
      compensationForTwoMonths: boolean;
      compensationForThreeMonths: boolean;
      artistWorkSchedule: string;
    };
  }>({
    validateInputOnChange: true,
    initialValues: {
      data: {
        minContractDurationWeeks: 0,
        artistWorkSchedule: '4/2',
        flightPaymentType: FlightPaymentType.Club,
        compensationForOneMonth: 0,
        compensationForTwoMonths: 0,
        compensationForThreeMonths: 0,
        freeReturnTicketAfterSixMonths: false,
        isTransferOrganizedByClub: false
      },
      additional: {
        artistWorkSchedule: '',
        compensationForOneMonth: false,
        compensationForTwoMonths: false,
        compensationForThreeMonths: false
      }
    },
    transformValues: (values) => ({
      ...values,
      data: {
        ...values.data,
        artistWorkSchedule:
          values.additional.artistWorkSchedule !== 'other'
            ? values.additional.artistWorkSchedule
            : values.data.artistWorkSchedule,
        compensationForOneMonth:
          values.data.flightPaymentType === FlightPaymentType.Compensation
            ? values.data.compensationForOneMonth
            : 0,
        compensationForTwoMonths:
          values.data.flightPaymentType === FlightPaymentType.Compensation
            ? values.data.compensationForTwoMonths
            : 0,
        compensationForThreeMonths:
          values.data.flightPaymentType === FlightPaymentType.Compensation
            ? values.data.compensationForThreeMonths
            : 0,
        freeReturnTicketAfterSixMonths:
          values.data.flightPaymentType === FlightPaymentType.Compensation
            ? values.data.freeReturnTicketAfterSixMonths
            : false
      }
    }),
    validate: zodResolver(contractTermsSchema)
  });

  const next = () => {
    navigate({
      to: '/clubOwner/$id/onboard/$step',
      params: {
        step: 'payments'
      }
    });
  };

  useEffect(() => {
    if (contractTermsQuery.data?.data) {
      form.initialize({
        data: { ...contractTermsQuery.data?.data },
        additional: {
          artistWorkSchedule:
            contractTermsQuery.data.data.artistWorkSchedule === '5/2' ||
            contractTermsQuery.data.data.artistWorkSchedule === '6/1'
              ? contractTermsQuery.data.data.artistWorkSchedule
              : 'other',
          compensationForOneMonth:
            contractTermsQuery.data.data.flightPaymentType ===
            FlightPaymentType.Compensation
              ? !!contractTermsQuery.data?.data.compensationForOneMonth
              : false,
          compensationForTwoMonths:
            contractTermsQuery.data.data.flightPaymentType ===
            FlightPaymentType.Compensation
              ? !!contractTermsQuery.data?.data.compensationForTwoMonths
              : false,
          compensationForThreeMonths:
            contractTermsQuery.data.data.flightPaymentType ===
            FlightPaymentType.Compensation
              ? !!contractTermsQuery.data?.data.compensationForThreeMonths
              : false
        }
      });
    }
  }, [contractTermsQuery.data?.data]);

  const onSubmit = form.onSubmit(async (values) => {
    await contractTermsMutation.mutateAsync(
      {
        params: {
          clubId,
          data: values.data
        }
      },
      {
        onSuccess: async () => {
          notifications.show({
            title: formatMessage({ id: 'notifications.successDataUpdate' }),
            ...notificationStyleConfig
          });
          await queryClient.invalidateQueries({
            queryKey: ['ClubProfileContractTerms', clubId]
          });
          next();
        }
      }
    );
  });

  const queries = [contractTermsQuery];
  const isLoading = queries.some((q) => q.isLoading);

  return {
    state: { isLoading, form },
    functions: { onSubmit }
  };
};
